export interface RoadsenseWorkbookFilenameInput {
  /** Customer-facing site name, as carried on the eligible rows. */
  siteName: string;
  /** Inclusive local calendar dates bounding the report period, `YYYY-MM-DD`. */
  periodStart: string;
  periodEnd: string;
  /**
   * An on-request export's cutoff and the site's zone. When the cutoff falls on
   * `periodEnd` (site-local), the name ends with its site-local time, e.g.
   * `… to 2026-11-03 14-45.xlsx`, since that day is still in progress.
   */
  cutoff?: { at: Date; timezone: string };
}

/** Windows-reserved characters, the POSIX separator, and C0/C1 control codes. */
// eslint-disable-next-line no-control-regex -- control codes are exactly what this replaces
const UNSAFE_IN_FILENAME = /[\\/:*?"<>|\u0000-\u001f\u007f-\u009f]/g;

/** A site name longer than this is malformed, not legitimate - cap it. */
const MAX_SITE_NAME_CHARS = 80;

/**
 * The name a customer saves one site's workbook as - NOT the S3 key's trailing
 * `<siteId>.xlsx`, which stays structural for the artifact guard to parse.
 */
export function roadsenseWorkbookFilename(input: RoadsenseWorkbookFilenameInput): string {
  const siteName = input.siteName
    // Unsafe characters become spaces, so a separator reads as a word break.
    .replace(UNSAFE_IN_FILENAME, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_SITE_NAME_CHARS)
    .trim();
  const period = `${input.periodStart} to ${input.periodEnd}${cutoffTime(input)}`;
  // A name that sanitises away to nothing drops its segment rather than
  // leaving the filename with a leading separator.
  const prefix = siteName.length > 0 ? `${siteName} - ` : "";
  return `${prefix}RoadSense - ${period}.xlsx`;
}

/** ` HH-mm` (site-local, `:` being unsafe in a filename) when the cutoff falls on `periodEnd`, else nothing. */
function cutoffTime(input: RoadsenseWorkbookFilenameInput): string {
  if (input.cutoff === undefined) return "";
  const { at, timezone } = input.cutoff;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  if (`${part("year")}-${part("month")}-${part("day")}` !== input.periodEnd) return "";
  return ` ${part("hour")}-${part("minute")}`;
}
